import type { LearningsPause } from 'definitions';

type GeneratingLearningsProps = { labelled: number; concluded: number; paused: LearningsPause | null };

/** Why writing has stopped, said as the person can act on it. */
const PAUSE_TEXT: Record<LearningsPause, string> = {
  'out-of-credits': "Learnings paused: your account's AI credits are used up",
  'monthly-cap': "Learnings paused until next month: this month's AI credits are used",
  'no-account': 'Learnings paused: this website has no account to pay for them',
};

/**
 * "Writing learnings: 40 of 212 tests", beside the Library's header while the cron works
 * through a website's concluded tests, or why it has stopped when the account may not spend
 * on AI. A polite live region, so a screen reader hears the count move without being
 * interrupted.
 */
export const GeneratingLearnings = ({ labelled, concluded, paused }: GeneratingLearningsProps) => {
  const count = (
    <>
      <span className="tabular-nums">{labelled}</span> of <span className="tabular-nums">{concluded}</span> tests
    </>
  );

  if (paused) {
    return (
      <span
        role="status"
        aria-live="polite"
        className="micro-label rounded-pill border-warn-line text-warn inline-flex items-center gap-2 border px-2.5 py-1"
      >
        <span aria-hidden="true" className="bg-warn size-1.5 rounded-full" />
        {PAUSE_TEXT[paused]} ({count} written)
      </span>
    );
  }

  return (
    <span
      role="status"
      aria-live="polite"
      className="micro-label rounded-pill border-accent-line text-accent inline-flex items-center gap-2 border px-2.5 py-1"
    >
      <span aria-hidden="true" className="bg-accent size-1.5 rounded-full motion-safe:animate-pulse" />
      Writing learnings: {count}
    </span>
  );
};
